import React, { useState, useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Box, List, ListItem, ListItemText, Typography, Chip, Alert, CircularProgress, Divider, Slider, Button, ButtonGroup, Dialog, DialogActions, DialogContent, DialogTitle, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip, TextField } from "@mui/material";
import { } from "@mui/icons-material";
import PropTypes from "prop-types";

import { updateLoopThemeAction } from "../../../../store/actions/loopActions";
import ThemePolarArea from "../../../Charts/ThemePolarArea";
import { global, scoreLevels } from "../../../../context/global";
import { Capitalize, Pluralize } from "../../../../functions/Formatters";

const IMPORTANCE_OPTIONS = [
    { value: 0, label: "Geen" },
    { value: 1, label: "Norm" },
    { value: 2, label: "Verbetering" },
    { value: 3, label: "Maximale inspanning" },
];

export default function LoopThemeSelector({ loopId, editable = true, currentLoopIsLastLoop=false }) {
    const dispatch = useDispatch();
    const { loopThemes, loading } = useSelector((state) => state.loop);
    const { kpis: kpisByThemeId, flatThemes } = useSelector((state) => state.themes);
    const [localImportance, setLocalImportance] = useState({});
    const [localDescription, setLocalDescription] = useState({});
    const [openDialog, setOpenDialog] = useState(false);
    const [dialogThemeId, setDialogThemeId] = useState(null);

    // Memoize theme lookup to prevent unnecessary re-computations
    const themeLookup = useMemo(() => {
        const lookup = {};
        loopThemes.forEach(lt => {
            if (flatThemes[lt.theme]) {
                lookup[lt.theme] = flatThemes[lt.theme]
            } 
        });
        return lookup;
    }, [loopThemes, loopId]);

    // Prepare data for ThemePolarArea chart
    const chartData = useMemo(() => {
        if (loopThemes.length === 0) return null;
        
        return {
            projectThemes: loopThemes.map(loopTheme => ({
                theme: loopTheme.theme,
                importance: loopTheme.importance
            }))
        };
    }, [loopThemes, localImportance, loopId]);

    // No fetching here; data comes embedded with the project payload

    const handleImportanceChange = useCallback((loopTheme, newValue) => {
        if (!editable) return;

        try {
            dispatch(updateLoopThemeAction(loopTheme.id, {
                ...loopTheme,
                importance: newValue,
            }));
        } catch (error) {
            console.error("Error updating theme importance:", error);
        }
    }, [editable, dispatch]);

    const handleSliderChange = useCallback((loopTheme, newValue) => {
        // Update local state immediately for responsive UI
        setLocalImportance(prev => ({
            ...prev,
            [loopTheme.id]: newValue
        }));
    }, []);

    const handleSliderChangeCommitted = useCallback((loopTheme, newValue) => {
        // Only call backend when slider interaction is complete
        handleImportanceChange(loopTheme, newValue);
        // Clear local state after API call
        setLocalImportance(prev => {
            const newState = { ...prev };
            return newState;
        });
    }, [handleImportanceChange]);

    const handleDescriptionChange = useCallback((loopTheme, newDescription) => {
        if (!editable) return;

        // Update local state immediately for responsive UI
        setLocalDescription(prev => ({
            ...prev,
            [loopTheme.id]: newDescription
        }));
    }, [editable]);

    const handleDescriptionBlur = useCallback((loopTheme, newDescription) => {
        if (!editable) return;

        try {
            dispatch(updateLoopThemeAction(loopTheme.id, {
                ...loopTheme,
                description: newDescription,
            }));
            // Clear local state after API call
            setLocalDescription(prev => {
                const newState = { ...prev };
                delete newState[loopTheme.id];
                return newState;
            });
        } catch (error) {
            console.error("Error updating theme description:", error);
        }
    }, [editable, dispatch]);

    // Deletion UI currently not shown here; keep action available for future use

    const getImportanceLabel = useCallback((importance) => {
        const option = IMPORTANCE_OPTIONS.find(opt => opt.value === importance);
        return option ? option.label : "Onbekend";
    }, []);

    const getImportanceColor = useCallback((importance) => {
        switch (importance) {
            case 0: return "default";
            case 1: return "primary";
            case 2: return "warning";
            case 3: return "error";
            default: return "default";
        }
    }, []);

    const getScaleType = useCallback((scaleType) => {
        switch (scaleType) {
            case 0: return "Score";
            case 1: return "Kwantitatief, per dijkvak";
            case 2: return "Kwantitatief, voor gehele project";
            default: return scaleType;
        }
    }, []);

    const dialogTheme = dialogThemeId != null ? flatThemes[dialogThemeId] : null;
    const dialogKpis = useMemo(() => {
        const kpiMap = dialogThemeId != null ? (kpisByThemeId[dialogThemeId] || {}) : {};
        return Object.values(kpiMap).sort((a, b) => a.name.localeCompare(b.name));
    }, [dialogThemeId, kpisByThemeId]);

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" p={3}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box>
            <Typography variant="h6" component="h2" gutterBottom>
                {Capitalize(Pluralize(global.theme))}
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
            Hier geef je voor de verschillende aspecten van de duurzaamheidsroos het ambitieniveau aan. Gebruik de slider om het ambitieniveau aan te geven.  
            </Typography>

            {loopThemes.length === 0 ? (
                <Alert severity="info">
                    Geen {Pluralize(global.theme)} toegewezen aan deze ontwerpronde.
                </Alert>
            ) : (
                <>
                    {/* Theme Polar Area Chart */}
                    {chartData && 
                    <Box height={260} width={"100%"} sx={{position: 'sticky', top: currentLoopIsLastLoop ? '85px' : '138px', zIndex: 5, backgroundColor: 'background.paper', py: "20px"}}> 
                        <ThemePolarArea valueData={chartData} maxWidth={true} />
                    </Box>
}
                    
                    <List sx={{ width: '100%', bgcolor: 'background.paper' }}>
                        {loopThemes.map((loopTheme, index) => {
                            const theme = themeLookup[loopTheme.theme];
                            
                            return (
                                <Box key={loopTheme.id}>
                                    <ListItem disablePadding>
                                            <ListItemText
                                                primary={
                                                    <Box display="flex" alignItems="center" gap={1}>
                                                        <Typography variant="body1" fontWeight="medium">
                                                            {theme?.name || `Onbekend ${global.theme}`}
                                                        </Typography>
                                                        <Chip
                                                            label={getImportanceLabel(localImportance[loopTheme.id] !== undefined ? localImportance[loopTheme.id] : loopTheme.importance)}
                                                            color={getImportanceColor(localImportance[loopTheme.id] !== undefined ? localImportance[loopTheme.id] : loopTheme.importance)}
                                                            size="small"
                                                            variant="outlined"
                                                        />
                                                        <Button
                                                            onClick={() => { setDialogThemeId(loopTheme.theme); setOpenDialog(true); }}
                                                            size="small"
                                                            variant="text"
                                                        >
                                                            Bekijk KPI's
                                                        </Button>
                                                    </Box>
                                                }
                                                secondary={
                                                    <Box>
                                                        <TextField
                                                            value={localDescription[loopTheme.id] !== undefined ? localDescription[loopTheme.id] : (loopTheme.description || '')}
                                                            onChange={(e) => handleDescriptionChange(loopTheme, e.target.value)}
                                                            onBlur={(e) => handleDescriptionBlur(loopTheme, e.target.value)}
                                                            placeholder="Voeg een beschrijving toe voor dit thema..."
                                                            variant="outlined"
                                                            size="small"
                                                            multiline
                                                            rows={2}
                                                            disabled={!editable}
                                                            sx={{ 
                                                                mt: 1,
                                                                width: '100%',
                                                                maxWidth: '600px',
                                                                '& .MuiOutlinedInput-root': {
                                                                    fontSize: '0.875rem',
                                                                }
                                                            }}
                                                        />
                                                        <Box sx={{ mt: 2, px: 1, ml: 2 }}>
                                                            <Slider
                                                                value={localImportance[loopTheme.id] !== undefined ? localImportance[loopTheme.id] : loopTheme.importance}
                                                                onChange={(e, newValue) => handleSliderChange(loopTheme, newValue)}
                                                                onChangeCommitted={(e, newValue) => handleSliderChangeCommitted(loopTheme, newValue)}
                                                                min={0}
                                                                max={3}
                                                                step={1}
                                                                marks={[
                                                                    { value: 0, label: 'Geen' },
                                                                    { value: 1, label: 'Norm' },
                                                                    { value: 2, label: 'Verbetering' },
                                                                    { value: 3, label: 'Maximale inspanning' },
                                                                ]}
                                                                disabled={!editable}
                                                                size="small"
                                                                sx={{ 
                                                                    maxWidth: '400px',
                                                                    mt: 1,
                                                                    '& .MuiSlider-markLabel': {
                                                                        fontSize: '0.75rem',
                                                                    }
                                                                }}
                                                            />
                                                        </Box>
                                                    </Box>
                                                }
                                            />
                                          
                                    </ListItem>
                                    {index < loopThemes.length - 1 && <Divider />}
                                </Box>
                            );
                        })}
                    </List>
                </>
            )}

          <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth={"md"} fullWidth>
                <DialogTitle>{dialogTheme?.name || `${global.theme}`}</DialogTitle>
                <DialogContent>
                    {dialogTheme?.description && (
                        <Typography paragraph>{dialogTheme.description}</Typography>
                    )}
                    <Typography paragraph>
                        {dialogTheme?.name || `Dit ${global.theme}`} bevat de volgende KPI's:
                    </Typography>
                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>KPI</TableCell>
                                    <TableCell>Eenheid</TableCell>
                                    <TableCell>Type</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {(dialogKpis || []).length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={2}>
                                            <Typography variant="body2" color="text.secondary">{`Geen KPI's beschikbaar voor dit ${global.theme}.`}</Typography>
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    dialogKpis.map((kpi) => (
                                        <TableRow key={`${dialogThemeId}_${kpi.id}`} sx={{ width: "100%" }}>
                                            <TableCell sx={{ paddingRight: "5px", paddingLeft: "5px" }}>
                                                <Tooltip title={kpi.description || ""}>
                                                    <Typography>{kpi.name}</Typography>
                                                </Tooltip>
                                            </TableCell>
                                            <TableCell sx={{ paddingRight: "5px", paddingLeft: "5px" }}>
                                                {kpi.unit ? (
                                                    <Typography>{kpi.unit}</Typography>
                                                ) : (
                                                    <Tooltip title={`${scoreLevels[0].label}, ${scoreLevels[1].label}, ${scoreLevels[2].label}, ${scoreLevels[3].label} of ${scoreLevels[4].label}`}>
                                                        <Typography>
                                                            Schaal van 1 {scoreLevels[0].label} tot 5 {scoreLevels[4].label}
                                                        </Typography>
                                                    </Tooltip>
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                {getScaleType(kpi.scale_type)}
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </DialogContent>
                <DialogActions>
                    <ButtonGroup>
                        <Button onClick={() => setOpenDialog(false)}>Sluiten</Button>
                    </ButtonGroup>
                </DialogActions>
            </Dialog>
        </Box>
    );
} 

LoopThemeSelector.propTypes = {
    editable: PropTypes.bool,
    loopId: PropTypes.number.isRequired,
};